/** Recipient token input for Compose (Dean, Oct 2026): type a company, borrower or SF number and pick from every address we hold
 *  for that account (Customer File contacts, people who've emailed us, LoanDisk borrower) — or just type an address. */
import { useEffect, useRef, useState } from "react";
import { Loader2, User, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function RecipientInput({ api, value, onChange, onPickCase, placeholder, testid = "recipients", autoFocus = false }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hi, setHi] = useState(0);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = text.trim();
    if (q.length < 2) { setRows([]); setOpen(false); return undefined; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.recipients(q);
        const taken = new Set(value.map((v) => v.email));
        setRows((r.people || []).filter((p) => !taken.has(p.email)));
        setOpen(true); setHi(0);
      } catch { setRows([]); } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = (tok) => {
    if (!tok?.email || value.some((v) => v.email === tok.email)) return;
    onChange([...value, tok]);
    if (tok.case && onPickCase) onPickCase(tok.case);
  };
  const addRaw = () => {
    const parts = text.split(/[;,]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return false;
    const next = [...value];
    parts.forEach((p) => { const email = p.toLowerCase(); if (!next.some((v) => v.email === email)) next.push({ email, name: "", raw: true, valid: EMAIL_RE.test(email) }); });
    onChange(next); setText(""); setOpen(false);
    return true;
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown" && open) { e.preventDefault(); setHi((h) => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp" && open) { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" || e.key === "Tab" || e.key === "," || e.key === ";") {
      if (open && rows[hi] && (e.key === "Enter" || e.key === "Tab")) { e.preventDefault(); add(rows[hi]); setText(""); setOpen(false); }
      else if (text.trim()) { e.preventDefault(); addRaw(); }
    } else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative flex-1 min-w-0">
      <div className={cn("flex flex-wrap items-center gap-1 min-h-8 rounded-md border border-input bg-white px-1.5 py-1 text-sm focus-within:ring-1 focus-within:ring-ring")} data-testid={`${testid}-box`}
        onClick={() => boxRef.current?.querySelector("input")?.focus()}>
        {value.map((v) => (
          <span key={v.email} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] max-w-[280px]",
            v.valid === false ? "border-rose-300 bg-rose-50 text-rose-700" : v.case ? "border-indigo-300 bg-indigo-50 text-indigo-900" : "border-slate-300 bg-slate-50 text-slate-800")}
            title={v.name ? `${v.name} <${v.email}>${v.case ? ` · ${v.case.sf} ${v.case.customer_name}` : ""}` : v.email} data-testid={`${testid}-token`}>
            <span className="truncate">{v.name ? `${v.name} <${v.email}>` : v.email}</span>
            <button type="button" className="text-slate-400 hover:text-rose-600 shrink-0" onClick={(e) => { e.stopPropagation(); onChange(value.filter((x) => x.email !== v.email)); }} data-testid={`${testid}-remove`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} onBlur={() => { if (text.trim() && EMAIL_RE.test(text.trim())) addRaw(); }}
          onFocus={() => rows.length && setOpen(true)} autoFocus={autoFocus}
          placeholder={value.length ? "" : placeholder} className="flex-1 min-w-[160px] h-6 bg-transparent outline-none text-sm placeholder:text-muted-foreground" data-testid={`${testid}-input`} />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" />}
      </div>
      {open && rows.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg" data-testid={`${testid}-menu`}>
          {rows.map((p, i) => (
            <button key={p.email} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { add(p); setText(""); setOpen(false); }}
              className={cn("w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-slate-50", i === hi && "bg-slate-100")} data-testid={`${testid}-option`}>
              <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate"><span className="font-medium">{p.name || p.email}</span>{p.name && <span className="text-slate-500"> &lt;{p.email}&gt;</span>}</span>
                <span className="block truncate text-[10px] text-slate-500">{p.role}{p.case ? ` · ${p.case.sf || ""} ${p.case.customer_name}` : ""}</span>
              </span>
              <Badge variant="outline" className="text-[9px] shrink-0">{p.source}</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
